import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@ant-design/pro-components";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import MMNavigationRoutes from "../../utils/navigationRoutes";
import { useQuery } from "@tanstack/react-query";
import { DynamicModuleService } from "@/services/APIs";
import CityCollabSettingService from "@/services/APIs/CityCollabSettingService";
import OrganizationProfileDetailsService from "@/services/APIs/OrganizationProfileDetails";
import MemberService from "@/services/APIs/MemberService";

const { Title, Text } = Typography;

function generateToken(len = 10) {
  // Since each byte becomes 2 hex chars, we need len/2 bytes
  const byteLength = Math.ceil(len / 2);
  const a = new Uint8Array(byteLength);
  crypto.getRandomValues(a);
  const refer_code = Array.from(a, b => b.toString(10).padStart(2, "0")).join(
    ""
  );
  // Trim to exact length in case of odd number
  return refer_code.substring(0, len);
}

const tenantId = localStorage.getItem("tenantId") ?? "";
const BASE_URL = window.location.origin.includes("localhost")
  ? "http://localhost:8080"
  : "https://membermanagement.universal-software.in";

const BASE_JOIN_URL = `${BASE_URL}/${tenantId}/mm/:chapterId?refer_code=`;

const SettingsPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { fname } = useParams<{ fname: string }>();
  const [saving, setSaving] = useState(false);
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const { data: settingsEditData } = useQuery({
    queryKey: ["settingsData", id],
    queryFn: () => CityCollabSettingService.getSettingById(id ?? ""),
    enabled: !!id,
  });

  const { data: memberData } = useQuery({
    queryKey: ["memberData", "all"],
    queryFn: () =>
      MemberService.getAllMembers({
        page: 1,
        pageSize: 1000,
        member_status: true,
      }),
  });

  // Extract array from settingsData to handle various response structures
  const { data: settingsData } = useQuery({
    queryKey: ["settingsData", "MM Organization"],
    queryFn: () =>
      DynamicModuleService.getGridCoByConfig(
        "MM Organization",
        1,
        10,
        ["id", "organizationname"],
        {}
      ),
  });

  const settingsDataArray = useMemo(() => {
    if (!settingsData) {
      return [];
    }
    if (Array.isArray(settingsData)) {
      return settingsData;
    }
    if (settingsData.results && Array.isArray(settingsData.results)) {
      return settingsData.results;
    }
    if (settingsData.data && Array.isArray(settingsData.data)) {
      return settingsData.data;
    }
    return [];
  }, [settingsData]);

  const memberOptions = useMemo(() => {
    if (!memberData) {
      return [];
    }
    // MemberService returns { results: [...] }
    const list = memberData.results || [];

    return list.map((m: any) => ({
      value: m.member_id ?? m.id,
      label: `${m.member_first_name ?? ""} ${m.member_last_name ?? ""}`.trim(),
    }));
  }, [memberData]);

  const selectedOrgId = Form.useWatch("organization_profile", form);

  // useEffect(() => {
  //   form.setFieldsValue({ membership_type: undefined });
  // }, [selectedOrgId, form]);

  const { data: departmentData, isFetching: loadingDepartments } = useQuery({
    queryKey: ["departments", selectedOrgId],
    queryFn: () =>
      OrganizationProfileDetailsService.getDepartmentListByOrganizationId(
        selectedOrgId ?? ""
      ),
    enabled: !!selectedOrgId, // 👈 only when org is chosen
  });

  const refer_code = useMemo(() => generateToken(), []);

  const inviteURL = useMemo(
    () =>
      `${BASE_JOIN_URL.replace(":chapterId", fname ?? "chapter")}${refer_code}`,
    [refer_code, fname]
  );

  useEffect(() => {
    form.setFieldsValue({ invite_url: inviteURL });
  }, [inviteURL, form]);

  useEffect(() => {
    if (settingsEditData) {
      form.setFieldsValue({
        organization_profile: settingsEditData?.organization_id,
        membership_type: settingsEditData?.membership_type_id,
        department: settingsEditData?.department_id,
        expires_days: settingsEditData?.expiry_days,
        max_uses: settingsEditData?.max_user,
        invite_url: `${BASE_JOIN_URL}${settingsEditData?.invite_code}`,
        user: settingsEditData?.user_id,
      });
    }
  }, [settingsEditData, form]);

  const handleCopy = async () => {
    const url: string = form.getFieldValue("invite_url");
    try {
      await navigator.clipboard.writeText(url);
      message.success("Invite URL copied");
    } catch {
      message.error("Copy failed");
    }
  };

  // const handleRegenerate = () => {
  //   setRefer_code(generateToken());
  //   message.info("Invite link regenerated");
  // };

  const onFinish = async (values: any) => {
    setSaving(true);
    try {
      const payload = {
        organization_id: values.organization_profile,
        membership_type_id: values.membership_type ?? null,
        department_id: values.department ?? null,
        expiry_days: values.expires_days,
        max_user: values.max_uses,
        invite_code: refer_code,
        user_id: values.user ?? null,
      };
      if (isEditMode) {
        const res = await CityCollabSettingService.updateSetting(id!, payload);
        if (res) {
          message.success("Settings updated");
          navigate("/mm" + MMNavigationRoutes.SETTINGS_LIST);
        }
      } else {
        const res = await CityCollabSettingService.createSetting(payload);
        if (res) {
          message.success("Settings saved");
          navigate("/mm" + MMNavigationRoutes.SETTINGS_LIST);
        }
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "Unable to save settings.";
      message.error(msg);
      console.error("createSetting error:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        className="custom-page-header"
        onBack={() => navigate("/mm" + MMNavigationRoutes.SETTINGS_LIST)}
        title={fname ? "Edit Settings" : "Membership Settings"}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          membership_type: undefined,
          department: undefined,
          expires_days: 14,
          max_uses: 100,
          invite_url: inviteURL,
        }}
      >
        <Card
          style={{ borderRadius: 12, border: "none", marginBottom: 16 }}
          bodyStyle={{ padding: 24, background: "", borderRadius: 12 }}
        >
          <div className="flex-col-1 flex gap-4">
            <Form.Item
              name="organization_profile"
              label="Organization  Name"
              style={{ width: "100%" }}
              rules={[
                {
                  required: true,
                  message: "Please select organization profile",
                },
              ]}
            >
              <Select
                placeholder="Organization Name"
                options={settingsDataArray.map((item: any) => ({
                  value: item.id,
                  label: item.organizationname ?? item.fname,
                }))}
                onSelect={value => {
                  form.setFieldsValue({ organization_profile: value });
                }}
                onChange={() => {
                  form.setFieldsValue({
                    membership_type: undefined,
                    department: undefined,
                  });
                }}
                allowClear
              />
            </Form.Item>

            <Form.Item name="user" label="User" style={{ width: "100%" }}>
              <Select
                placeholder="Select User"
                options={memberOptions}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
            {/* <Form.Item
              name="membership_type"
              label="Membership Name"
              style={{ width: "100%" }}
            >
              <Select
                placeholder="Membership Name"
                disabled={!selectedOrgId} // disable until org chosen
                loading={loadingMemberships} // show loading state
                options={membershipTypesData?.map((item: any) => ({
                  value: item.id,
                  label: item.membershipname,
                }))}
                allowClear
              />
            </Form.Item> */}
            <Form.Item
              name="department"
              label="Department"
              style={{ width: "100%" }}
            >
              <Select
                placeholder="Department"
                disabled={!selectedOrgId} // disable until org chosen
                loading={loadingDepartments} // show loading state
                options={departmentData?.map((item: any) => ({
                  value: item.id,
                  label: item.department,
                }))}
                allowClear
              />
            </Form.Item>
          </div>

          <div style={{ marginTop: 12, marginBottom: 8 }}>
            <Title level={5} style={{ margin: 0, color: "#5e5ce6" }}>
              Invite Link
            </Title>
            <Text type="secondary">Control join token lifetime and usage</Text>
          </div>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="expires_days"
                label="Expires (days)"
                rules={[
                  { required: true, message: "Please enter days" },
                  { type: "number", min: 1, max: 365, message: "1–365 days" },
                ]}
              >
                <InputNumber min={1} max={365} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="max_uses"
                label="Max Users"
                rules={[{ required: true, message: "Please select max uses" }]}
              >
                <InputNumber min={1} max={365} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12} align="bottom">
            <Col flex="auto">
              <Form.Item name="invite_url" label="Current Invite URL">
                <Input readOnly />
              </Form.Item>
            </Col>
            <Col className="mb-[10px] flex justify-end">
              <Button onClick={handleCopy}>Copy</Button>
            </Col>
          </Row>

          <Text type="secondary">
            Regeneration invalidates the old link immediately
          </Text>
        </Card>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Space>
            <Button
              onClick={() => navigate("/mm" + MMNavigationRoutes.SETTINGS_LIST)}
              loading={saving}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {isEditMode ? "Update Setting" : "Save Setting"}
            </Button>
          </Space>
        </div>
      </Form>
    </>
  );
};

export default SettingsPage;
